import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api-client'
import { X } from 'lucide-react'

interface AddCompetitorModalProps {
  open: boolean
  onClose: () => void
}

function normalizeAsin(value: string): string {
  return value.trim().toUpperCase()
}

export default function AddCompetitorModal({ open, onClose }: AddCompetitorModalProps) {
  const queryClient = useQueryClient()
  const brandNameInputRef = useRef<HTMLInputElement>(null)
  const [brandName, setBrandName] = useState('')
  const [amazonAsin, setAmazonAsin] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }

    window.addEventListener('keydown', closeOnEscape)
    window.requestAnimationFrame(() => brandNameInputRef.current?.focus())

    return () => {
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [isSubmitting, onClose, open])

  useEffect(() => {
    if (!open) {
      setBrandName('')
      setAmazonAsin('')
      setNotes('')
      setError(null)
      setIsSubmitting(false)
    }
  }, [open])

  if (!open) {
    return null
  }

  const canSubmit = brandName.trim().length > 0 && !isSubmitting

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const normalizedBrand = brandName.trim()
      const normalizedAsin = normalizeAsin(amazonAsin)
      const normalizedNotes = notes.trim()
      const mergedNotes = [normalizedNotes, normalizedAsin ? `Amazon ASIN: ${normalizedAsin}` : null]
        .filter(Boolean)
        .join('\n')

      await apiClient.post('/competitive/competitors', {
        brand_name: normalizedBrand,
        amazon_brand_name: normalizedBrand,
        notes: mergedNotes || null,
        threat_tier: 'watch',
        tiktok_handle: null,
      })

      await queryClient.invalidateQueries({ queryKey: ['competitors'] })
      onClose()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to add competitor')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-slate-900/40" onClick={() => !isSubmitting && onClose()} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-competitor-title"
          className="w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <h3 id="add-competitor-title" className="text-lg font-semibold text-slate-900">
              Add Competitor
            </h3>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Close add competitor modal"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form className="space-y-4 px-5 py-4" onSubmit={(event) => void handleSubmit(event)}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <div>
              <label htmlFor="competitor-brand-name" className="mb-1 block text-sm font-medium text-slate-700">
                Brand Name
              </label>
              <input
                id="competitor-brand-name"
                ref={brandNameInputRef}
                type="text"
                value={brandName}
                onChange={(event) => setBrandName(event.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                required
              />
            </div>

            <div>
              <label htmlFor="competitor-amazon-asin" className="mb-1 block text-sm font-medium text-slate-700">
                Amazon ASIN
              </label>
              <input
                id="competitor-amazon-asin"
                type="text"
                value={amazonAsin}
                onChange={(event) => setAmazonAsin(event.target.value)}
                placeholder="e.g. B0123456789"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              />
            </div>

            <div>
              <label htmlFor="competitor-notes" className="mb-1 block text-sm font-medium text-slate-700">
                Notes
              </label>
              <textarea
                id="competitor-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={4}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              />
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-md border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Adding...' : 'Add Competitor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
